import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { adminApi, api } from '../utils/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

const DEFAULT_PLATFORM_REACH = {
  linkedin: 1200,
  youtube: 3000,
  instagram: 1800,
  tiktok: 2200,
  x: 900,
};

const HOOK_STYLES = ['outcome', 'contrarian', 'stat', 'comparison', 'secret'];

const toDate = (value) => {
  const d = new Date(value || '');
  return Number.isNaN(d.getTime()) ? null : d;
};

const isSameMonth = (date, base = new Date()) => (
  date && date.getMonth() === base.getMonth() && date.getFullYear() === base.getFullYear()
);

const isLastMonth = (date, base = new Date()) => {
  if (!date) return false;
  const target = new Date(base.getFullYear(), base.getMonth() - 1, 1);
  return date.getMonth() === target.getMonth() && date.getFullYear() === target.getFullYear();
};

const isThisWeek = (date) => {
  if (!date) return false;
  const now = new Date();
  const weekStart = new Date(now);
  const day = weekStart.getDay();
  weekStart.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  weekStart.setHours(0, 0, 0, 0);
  return date >= weekStart;
};

const parseMoney = (value) => {
  const num = Number(value);
  if (!Number.isNaN(num)) return num;
  const extracted = String(value || '').match(/\d+(\.\d+)?/);
  return extracted ? Number(extracted[0]) : 0;
};

const parseCommissionRate = (value) => {
  const match = String(value || '').match(/(\d+(\.\d+)?)\s*%/);
  if (match) return Number(match[1]) / 100;
  const asNum = Number(value);
  return Number.isNaN(asNum) ? 0.05 : asNum;
};

const numberFmt = new Intl.NumberFormat();
const moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const chartTheme = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: '#A0A0A5',
        font: { family: 'JetBrains Mono' },
      },
    },
    tooltip: {
      backgroundColor: '#0B0C10',
      borderColor: '#FFE800',
      borderWidth: 1,
      titleColor: '#FFE800',
      bodyColor: '#FFFFFF',
    },
  },
  scales: {
    x: {
      grid: { color: 'rgba(255,255,255,0.06)' },
      ticks: { color: '#A0A0A5', font: { family: 'JetBrains Mono' } },
    },
    y: {
      grid: { color: 'rgba(255,255,255,0.06)' },
      ticks: { color: '#A0A0A5', font: { family: 'JetBrains Mono' } },
    },
  },
};

const MarketingAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [queueItems, setQueueItems] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [tools, setTools] = useState([]);
  const [settings, setSettings] = useState({});
  const [performanceRows, setPerformanceRows] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [queueRes, commissionsRes, toolsRes, settingsRes, performanceRes] = await Promise.all([
          adminApi.getQueue().catch(() => ({ data: { items: [] } })),
          adminApi.getCommissions().catch(() => ({ data: [] })),
          adminApi.getTools().catch(() => ({ data: [] })),
          adminApi.getSettings().catch(() => ({ data: {} })),
          api.get('/admin/performance').catch(() => ({ data: [] })),
        ]);

        const queueData = Array.isArray(queueRes?.data) ? queueRes.data : (queueRes?.data?.items || []);
        setQueueItems(queueData);
        setCommissions(Array.isArray(commissionsRes?.data) ? commissionsRes.data : []);
        setTools(Array.isArray(toolsRes?.data) ? toolsRes.data : []);
        setSettings(settingsRes?.data || {});
        setPerformanceRows(Array.isArray(performanceRes?.data) ? performanceRes.data : []);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const analytics = useMemo(() => {
    const now = new Date();
    const platformReach = {
      ...DEFAULT_PLATFORM_REACH,
      ...(settings.platform_reach || {}),
      ...(settings.platform_reach_map || {}),
    };

    const publishedThisMonth = queueItems.filter((q) => {
      const dt = toDate(q.published_at || q.updated_at || q.created_at);
      return String(q.status || '').toLowerCase() === 'published' && isSameMonth(dt, now);
    });

    const piecesMonth = publishedThisMonth.length;

    const estimatedReach = publishedThisMonth.reduce((sum, row) => {
      const platforms = Array.isArray(row.platforms) && row.platforms.length ? row.platforms : ['linkedin'];
      const rowReach = platforms.reduce((s, p) => s + (platformReach[String(p || '').toLowerCase()] || 1000), 0);
      return sum + rowReach;
    }, 0);

    const perfThisMonth = performanceRows.filter((p) => {
      const dt = toDate(p.created_at || p.timestamp || p.date);
      return isSameMonth(dt, now);
    });

    const affiliateClicksMonthFromPerf = perfThisMonth.reduce((sum, p) => {
      const v = Number(p.affiliate_clicks || p.affiliateClicks || p.clicks || 0);
      return sum + (Number.isNaN(v) ? 0 : v);
    }, 0);

    const queueClicksMonth = publishedThisMonth.reduce((sum, q) => sum + Number(q.clicks || q.metrics?.clicks || 0), 0);
    const affiliateClicksMonth = affiliateClicksMonthFromPerf || queueClicksMonth;

    const revenueMonth = commissions
      .filter((c) => isSameMonth(toDate(c.created_at || c.updated_at), now))
      .reduce((sum, c) => sum + parseMoney(c.amount), 0);

    const revenuePerPiece = piecesMonth ? revenueMonth / piecesMonth : 0;

    const weekLabels = [];
    const weeklyContent = [];
    const weeklyClicks = [];
    const weeklyRevenue = [];
    for (let i = 11; i >= 0; i -= 1) {
      const end = new Date(now);
      end.setDate(now.getDate() - i * 7);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);

      weekLabels.push(`W-${i}`);

      const contentCount = queueItems.filter((q) => {
        const dt = toDate(q.created_at || q.updated_at);
        return dt && dt >= start && dt <= end;
      }).length;

      const clicks = (performanceRows.length ? performanceRows : queueItems).reduce((sum, item) => {
        const dt = toDate(item.created_at || item.timestamp || item.updated_at);
        if (!dt || dt < start || dt > end) return sum;
        return sum + Number(item.affiliate_clicks || item.affiliateClicks || item.clicks || item.metrics?.clicks || 0);
      }, 0);

      const revenue = commissions.reduce((sum, c) => {
        const dt = toDate(c.created_at || c.updated_at);
        if (!dt || dt < start || dt > end) return sum;
        return sum + parseMoney(c.amount);
      }, 0);

      weeklyContent.push(contentCount);
      weeklyClicks.push(clicks);
      weeklyRevenue.push(Math.round(revenue));
    }

    const postsThisWeekRows = queueItems.filter((q) => isThisWeek(toDate(q.created_at || q.updated_at)));
    const platformPerfMap = new Map();

    postsThisWeekRows.forEach((row) => {
      const platforms = Array.isArray(row.platforms) && row.platforms.length ? row.platforms : ['linkedin'];
      platforms.forEach((p) => {
        const platform = String(p || 'linkedin').toLowerCase();
        const existing = platformPerfMap.get(platform) || {
          platform,
          posts: 0,
          reach: 0,
          clickThroughs: 0,
          typeCounter: new Map(),
          score: 0,
        };

        existing.posts += 1;
        existing.reach += platformReach[platform] || 1000;
        const rowClicks = Number(row.clicks || row.metrics?.clicks || 0);
        existing.clickThroughs += Number.isNaN(rowClicks) ? 0 : rowClicks;

        const ctype = String(row.content_type || 'unknown').toLowerCase();
        existing.typeCounter.set(ctype, (existing.typeCounter.get(ctype) || 0) + 1);

        platformPerfMap.set(platform, existing);
      });
    });

    performanceRows.forEach((p) => {
      const platform = String(p.platform || '').toLowerCase();
      if (!platform) return;
      const existing = platformPerfMap.get(platform) || {
        platform,
        posts: 0,
        reach: 0,
        clickThroughs: 0,
        typeCounter: new Map(),
        score: 0,
      };

      existing.clickThroughs += Number(p.affiliate_clicks || p.affiliateClicks || p.clicks || 0) || 0;
      existing.posts += Number(p.posts || 0) || 0;
      existing.reach += Number(p.est_reach || p.estimatedReach || 0) || 0;
      if (p.content_type) {
        const ctype = String(p.content_type).toLowerCase();
        existing.typeCounter.set(ctype, (existing.typeCounter.get(ctype) || 0) + 1);
      }

      platformPerfMap.set(platform, existing);
    });

    const platformRows = [...platformPerfMap.values()].map((row) => {
      const bestType = [...row.typeCounter.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || 'n/a';
      const score = row.clickThroughs + row.reach * 0.03;
      return { ...row, bestType, score };
    }).sort((a, b) => b.score - a.score);

    const bestPlatform = platformRows[0]?.platform;

    const coverage = tools.map((tool) => {
      const rows = queueItems.filter((q) => String(q.tool_id) === String(tool.id));
      const hasThisMonth = rows.some((r) => isSameMonth(toDate(r.created_at || r.updated_at), now));
      const hasLastMonth = rows.some((r) => isLastMonth(toDate(r.created_at || r.updated_at), now));
      return {
        id: tool.id,
        name: tool.name,
        slug: tool.slug,
        status: hasThisMonth ? 'covered_this_month' : hasLastMonth ? 'covered_last_month' : 'never',
      };
    });

    const coverageSet = new Set(queueItems.map((q) => String(q.tool_id)));
    const gaps = tools
      .filter((tool) => !coverageSet.has(String(tool.id)))
      .map((tool) => {
        const affiliate = tool.affiliate_link || {};
        const commission = parseCommissionRate(affiliate.commission);
        const cookieDays = Number(affiliate.cookie_days || 30);
        return {
          id: tool.id,
          name: tool.name,
          score: Math.round(commission * Math.max(cookieDays, 1) * 1000) / 1000,
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    const hookMap = new Map(HOOK_STYLES.map((k) => [k, []]));
    performanceRows.forEach((row) => {
      const style = String(row.hook_style || '').toLowerCase();
      if (!hookMap.has(style)) return;
      const value = Number(row.completion_rate || row.avg_completion_rate || row.completion || 0);
      if (!Number.isNaN(value) && value > 0) hookMap.get(style).push(value);
    });

    if (!performanceRows.length) {
      queueItems.forEach((row) => {
        const style = String(row.content?.hook_style || row.hook_style || '').toLowerCase();
        if (!hookMap.has(style)) return;
        const value = Number(row.completion_rate || 0);
        if (!Number.isNaN(value) && value > 0) hookMap.get(style).push(value);
      });
    }

    const hookPerf = HOOK_STYLES.map((style) => {
      const values = hookMap.get(style) || [];
      const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
      return { style, avg: Math.round(avg * 10) / 10 };
    });

    const winningHook = [...hookPerf].sort((a, b) => b.avg - a.avg)[0]?.style || 'n/a';

    const piecesThisWeek = postsThisWeekRows.length;
    const weeklyTarget = Number(settings.weekly_content_target || settings.weekly_target || 21);
    const weeklyProgress = Math.min(100, Math.round((piecesThisWeek / Math.max(1, weeklyTarget)) * 100));

    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const projectedMonth = Math.round((piecesThisWeek / 7) * daysInMonth);

    return {
      kpis: {
        piecesMonth,
        estimatedReach,
        affiliateClicksMonth,
        revenueMonth,
        revenuePerPiece,
      },
      trend: {
        labels: weekLabels,
        output: weeklyContent,
        clicks: weeklyClicks,
        revenue: weeklyRevenue,
      },
      platformRows,
      bestPlatform,
      coverage,
      coverageGaps: gaps,
      hookPerf,
      winningHook,
      velocity: {
        piecesThisWeek,
        weeklyTarget,
        weeklyProgress,
        projectedMonth,
      },
    };
  }, [commissions, performanceRows, queueItems, settings, tools]);

  const trendData = useMemo(() => ({
    labels: analytics.trend.labels,
    datasets: [
      {
        label: 'Content Output',
        data: analytics.trend.output,
        borderColor: '#FFE800',
        backgroundColor: '#FFE800',
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.3,
      },
      {
        label: 'Affiliate Clicks',
        data: analytics.trend.clicks,
        borderColor: '#39FF14',
        backgroundColor: '#39FF14',
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.3,
      },
      {
        label: 'Revenue',
        data: analytics.trend.revenue,
        borderColor: '#00F0FF',
        backgroundColor: '#00F0FF',
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.3,
      },
    ],
  }), [analytics.trend]);

  const hookData = useMemo(() => ({
    labels: analytics.hookPerf.map((h) => h.style),
    datasets: [
      {
        label: 'Avg completion rate',
        data: analytics.hookPerf.map((h) => h.avg),
        backgroundColor: analytics.hookPerf.map((h) => (h.style === analytics.winningHook ? '#FFE800' : '#39FF14')),
        borderColor: analytics.hookPerf.map((h) => (h.style === analytics.winningHook ? '#FFE800' : '#39FF14')),
        borderWidth: 1,
      },
    ],
  }), [analytics.hookPerf, analytics.winningHook]);

  if (loading) {
    return <div className="terminal-grid rounded-none p-6 text-text-secondary">Loading analytics...</div>;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Published this month" value={numberFmt.format(analytics.kpis.piecesMonth)} />
        <StatCard title="Estimated reach" value={numberFmt.format(analytics.kpis.estimatedReach)} />
        <StatCard title="Affiliate clicks" value={numberFmt.format(analytics.kpis.affiliateClicksMonth)} />
        <StatCard title="Revenue" value={moneyFmt.format(analytics.kpis.revenueMonth)} />
        <StatCard title="Revenue per piece" value={moneyFmt.format(analytics.kpis.revenuePerPiece)} highlight />
      </div>

      <div className="terminal-grid rounded-none p-5">
        <h3 className="text-lg text-white font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          12-week ROI trend
        </h3>
        <div className="h-[320px]">
          <Line data={trendData} options={chartTheme} />
        </div>
      </div>

      <div className="terminal-grid rounded-none p-5 overflow-x-auto">
        <h3 className="text-lg text-white font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          Platform performance
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-white/10 text-text-muted">
              <th className="py-2">Platform</th>
              <th className="py-2">Posts this week</th>
              <th className="py-2">Est. reach</th>
              <th className="py-2">Click-throughs</th>
              <th className="py-2">Best content type</th>
            </tr>
          </thead>
          <tbody>
            {analytics.platformRows.map((row) => (
              <tr
                key={row.platform}
                className={row.platform === analytics.bestPlatform ? 'bg-cyber-yellow/10 border-l-2 border-cyber-yellow' : 'border-b border-white/5'}
              >
                <td className="py-2 capitalize text-white">{row.platform}</td>
                <td className="py-2 text-text-secondary">{numberFmt.format(row.posts)}</td>
                <td className="py-2 text-text-secondary">{numberFmt.format(Math.round(row.reach))}</td>
                <td className="py-2 text-text-secondary">{numberFmt.format(Math.round(row.clickThroughs))}</td>
                <td className="py-2 text-text-secondary">{row.bestType}</td>
              </tr>
            ))}
            {!analytics.platformRows.length && (
              <tr>
                <td colSpan={5} className="py-3 text-text-secondary">No platform performance data yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="terminal-grid rounded-none p-5">
        <h3 className="text-lg text-white font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          Tool content coverage
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
          {analytics.coverage.map((tool) => {
            const color = tool.status === 'covered_this_month' ? '#39FF14' : tool.status === 'covered_last_month' ? '#FFE800' : '#ff4d4d';
            return (
              <div key={tool.id} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-text-secondary truncate" title={tool.name}>{tool.name}</span>
              </div>
            );
          })}
        </div>

        <h4 className="text-sm text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Coverage gaps (top 5)</h4>
        <div className="space-y-1">
          {analytics.coverageGaps.map((gap) => (
            <div key={gap.id} className="text-xs text-text-secondary flex justify-between border-b border-white/5 py-1">
              <span>{gap.name}</span>
              <span className="text-cyber-yellow font-mono">score {gap.score}</span>
            </div>
          ))}
          {!analytics.coverageGaps.length && <div className="text-xs text-text-secondary">No major coverage gaps detected.</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="terminal-grid rounded-none p-5">
          <h3 className="text-lg text-white font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Hook performance ({analytics.winningHook} winning)
          </h3>
          <div className="h-[280px]">
            <Bar data={hookData} options={chartTheme} />
          </div>
        </div>

        <div className="terminal-grid rounded-none p-5">
          <h3 className="text-lg text-white font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Weekly velocity
          </h3>
          <div className="text-sm text-text-secondary mb-2">
            You created <span className="text-white font-semibold">{analytics.velocity.piecesThisWeek}</span> pieces this week vs your target of{' '}
            <span className="text-cyber-yellow font-semibold">{analytics.velocity.weeklyTarget}</span>
          </div>
          <div className="w-full h-3 bg-white/10 rounded-none overflow-hidden mb-3">
            <div className="h-full bg-cyber-yellow" style={{ width: `${analytics.velocity.weeklyProgress}%` }} />
          </div>
          <div className="text-sm text-text-secondary">
            At this pace you&apos;ll publish <span className="text-laser-green font-semibold">{analytics.velocity.projectedMonth}</span> pieces this month.
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, highlight = false }) => (
  <div className={`terminal-grid rounded-none p-4 ${highlight ? 'border-cyber-yellow/40' : ''}`}>
    <div className="text-xs text-text-muted uppercase tracking-wide">{title}</div>
    <div className={`mt-2 text-2xl font-bold ${highlight ? 'text-cyber-yellow' : 'text-white'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
      {value}
    </div>
  </div>
);

export default MarketingAnalytics;
